import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    type KeyboardEvent,
    type PointerEvent,
    type ReactElement,
    type ReactNode,
} from "react";
import type { AnimationPlaybackControls } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import { clsx } from "clsx";
import { motion as motionTokens } from "@tokens/index";
import { capturePointer, pressHandlers, releasePointer, setPressed } from "./press";
import { placeThumb, px, springThumb, thumbTransform } from "./thumbMotion";
import { useReducedMotionPreference } from "./useReducedMotionPreference";
import pressable from "./pressable.module.css";
import styles from "./SegmentedControl.module.css";

/**
 * SegmentedControl: one choice from a short, fixed set.
 *
 * Fully controlled. `role="radiogroup"` with `role="radio"` segments and a
 * roving tab stop; Arrow keys, Home and End move the selection like native
 * radios, Space/Enter select the focused segment.
 *
 * The thumb is one absolutely positioned element that springs to the selected
 * segment through `thumbMotion` (a composed `transform` plus `width`, both Web
 * Animations, measured with `ResizeObserver` so it stays correct after resize
 * or font load). Press the selected thumb and drag
 * to slide the selection across segments; the thumb follows the pointer and
 * snaps on release. A click on any segment selects it directly.
 *
 * The only internal state is the transient drag position. Reduced motion
 * (OS or `html[data-motion="reduced"]`) makes every thumb move instant.
 */

export interface SegmentedControlOption<T extends string> {
    value: T;
    label?: ReactNode;
    icon?: LucideIcon;
    /** Accessible name. Required when the segment has no visible label. */
    ariaLabel?: string;
    disabled?: boolean;
}

export type SegmentedControlSize = "sm" | "md";
/** `neutral` = raised surface thumb (default). `accent` = filled accent thumb. */
export type SegmentedControlTone = "neutral" | "accent";

export interface SegmentedControlProps<T extends string> {
    options: readonly SegmentedControlOption<T>[];
    value: T;
    onChange: (value: T) => void;
    size?: SegmentedControlSize;
    tone?: SegmentedControlTone;
    /** Plain-text group name. Use `labelledBy` when a visible label exists. */
    ariaLabel?: string;
    labelledBy?: string;
    /** Id of the text that explains the group, for example a row description. */
    "aria-describedby"?: string;
    fullWidth?: boolean;
    /** Hide labels; each option then needs `ariaLabel`. */
    iconOnly?: boolean;
    disabled?: boolean;
    className?: string;
}

interface SegmentRect {
    readonly x: number;
    readonly width: number;
}

interface DragState {
    pointerId: number;
    startClientX: number;
    startX: number;
    moved: boolean;
    hoverIndex: number;
}

// Pointer travel before a press on the thumb becomes a drag.
const DRAG_SLOP_PX = 3;

const iconSize: Record<SegmentedControlSize, number> = { sm: 14, md: 16 };

const clamp = (value: number, min: number, max: number): number =>
    Math.min(Math.max(value, min), max);

export function SegmentedControl<T extends string>({
    options,
    value,
    onChange,
    size = "md",
    tone = "neutral",
    ariaLabel,
    labelledBy,
    "aria-describedby": ariaDescribedBy,
    fullWidth = false,
    iconOnly = false,
    disabled = false,
    className,
}: SegmentedControlProps<T>): ReactElement {
    const rootRef = useRef<HTMLDivElement>(null);
    const segmentRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const rectsRef = useRef<SegmentRect[]>([]);
    const dragRef = useRef<DragState | null>(null);
    const suppressClickRef = useRef(false);
    const thumbRef = useRef<HTMLDivElement>(null);
    const thumbAnimation = useRef<AnimationPlaybackControls | null>(null);
    const widthAnimation = useRef<AnimationPlaybackControls | null>(null);
    const mountedRef = useRef(false);

    // The thumb's target geometry. The element itself holds the in-flight value.
    const thumbXRef = useRef(0);
    const thumbWidthRef = useRef(0);

    const reduced = useReducedMotionPreference();
    const reducedRef = useRef(reduced);
    reducedRef.current = reduced;

    const selectedIndex = options.findIndex((option) => option.value === value);
    const selectedIndexRef = useRef(selectedIndex);
    selectedIndexRef.current = selectedIndex;

    const isEnabled = useCallback(
        (index: number): boolean => {
            const option = options[index];
            return option !== undefined && option.disabled !== true && !disabled;
        },
        [options, disabled],
    );

    const stopThumb = useCallback((): void => {
        thumbAnimation.current?.stop();
        thumbAnimation.current = null;
        widthAnimation.current?.stop();
        widthAnimation.current = null;
    }, []);

    const moveThumb = useCallback(
        (index: number, animated: boolean): void => {
            const rect = rectsRef.current[index];
            const el = thumbRef.current;
            stopThumb();
            if (el === null) return;
            if (rect === undefined || rect.width === 0) {
                el.style.opacity = "0";
                return;
            }
            thumbXRef.current = rect.x;
            thumbWidthRef.current = rect.width;
            const target = { transform: thumbTransform(rect.x), width: px(rect.width) };
            if (!animated || reducedRef.current) {
                placeThumb(el, target);
            } else {
                thumbAnimation.current = springThumb(el, target, motionTokens.springs.thumb);
            }
            el.style.opacity = "1";
        },
        [stopThumb],
    );

    // `offsetLeft` is relative to the root's padding edge, which is where the
    // thumb is positioned. No rect subtraction, no scroll dependency.
    const measure = useCallback((): void => {
        rectsRef.current = segmentRefs.current
            .slice(0, options.length)
            .map((el) =>
                el === null ? { x: 0, width: 0 } : { x: el.offsetLeft, width: el.offsetWidth },
            );
    }, [options.length]);

    useLayoutEffect(() => {
        measure();
        moveThumb(selectedIndexRef.current, false);
    }, [measure, moveThumb, size, iconOnly, fullWidth]);

    useEffect(() => {
        if (!mountedRef.current) {
            mountedRef.current = true;
            return;
        }
        if (dragRef.current === null) {
            moveThumb(selectedIndex, true);
        }
    }, [selectedIndex, moveThumb]);

    useEffect(() => {
        const root = rootRef.current;
        if (root === null || typeof ResizeObserver !== "function") return;
        const observer = new ResizeObserver(() => {
            measure();
            if (dragRef.current === null) {
                moveThumb(selectedIndexRef.current, false);
            }
        });
        observer.observe(root);
        for (const el of segmentRefs.current) {
            if (el !== null) observer.observe(el);
        }
        return () => observer.disconnect();
    }, [measure, moveThumb, options.length]);

    const select = (index: number): void => {
        const option = options[index];
        if (option === undefined || !isEnabled(index)) return;
        if (option.value !== value) onChange(option.value);
    };

    // ── Keyboard: arrows move focus and selection together, like native radios.

    const stepEnabled = (from: number, direction: 1 | -1): number => {
        const count = options.length;
        for (let step = 1; step <= count; step += 1) {
            const next = (from + direction * step + count * step) % count;
            if (isEnabled(next)) return next;
        }
        return from;
    };

    const edgeEnabled = (direction: 1 | -1): number => {
        const indices = options.map((_, i) => i);
        if (direction === -1) indices.reverse();
        return indices.find(isEnabled) ?? selectedIndex;
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        if (disabled) return;
        let next: number | null = null;
        switch (event.key) {
            case "ArrowRight":
            case "ArrowDown":
                next = stepEnabled(selectedIndex, 1);
                break;
            case "ArrowLeft":
            case "ArrowUp":
                next = stepEnabled(selectedIndex, -1);
                break;
            case "Home":
                next = edgeEnabled(1);
                break;
            case "End":
                next = edgeEnabled(-1);
                break;
            default:
                return;
        }
        event.preventDefault();
        segmentRefs.current[next]?.focus({ preventScroll: true });
        select(next);
    };

    // ── Pointer drag on the selected thumb.

    const nearestEnabledIndex = (center: number): number => {
        let best = selectedIndexRef.current;
        let bestDistance = Number.POSITIVE_INFINITY;
        rectsRef.current.forEach((rect, index) => {
            if (!isEnabled(index)) return;
            const distance = Math.abs(rect.x + rect.width / 2 - center);
            if (distance < bestDistance) {
                bestDistance = distance;
                best = index;
            }
        });
        return best;
    };

    const handlePointerDown = (event: PointerEvent<HTMLDivElement>): void => {
        if (disabled || event.button !== 0) return;
        const target = (event.target as HTMLElement).closest<HTMLButtonElement>('[role="radio"]');
        const index = target === null ? -1 : segmentRefs.current.indexOf(target);
        if (index === -1 || index !== selectedIndex) return;
        if (rectsRef.current[index] === undefined) return;

        stopThumb();
        dragRef.current = {
            pointerId: event.pointerId,
            startClientX: event.clientX,
            startX: thumbXRef.current,
            moved: false,
            hoverIndex: index,
        };
        capturePointer(event.currentTarget, event.pointerId);
        event.currentTarget.setAttribute("data-dragging", "");
    };

    const handlePointerMove = (event: PointerEvent<HTMLDivElement>): void => {
        const drag = dragRef.current;
        if (drag === null || drag.pointerId !== event.pointerId) return;
        const dx = event.clientX - drag.startClientX;
        if (!drag.moved && Math.abs(dx) < DRAG_SLOP_PX) return;
        drag.moved = true;
        suppressClickRef.current = true;

        const rects = rectsRef.current;
        const first = rects[0];
        const last = rects[rects.length - 1];
        if (first === undefined || last === undefined) return;

        const el = thumbRef.current;
        if (el === null) return;
        const currentWidth = thumbWidthRef.current;
        const nextX = clamp(drag.startX + dx, first.x, last.x + last.width - currentWidth);
        thumbXRef.current = nextX;
        // The pointer drives the transform directly; only the width springs.
        placeThumb(el, { transform: thumbTransform(nextX) });

        const hover = nearestEnabledIndex(nextX + currentWidth / 2);
        if (hover !== drag.hoverIndex) {
            drag.hoverIndex = hover;
            const rect = rects[hover];
            if (rect !== undefined) {
                widthAnimation.current?.stop();
                thumbWidthRef.current = rect.width;
                const target = { width: px(rect.width) };
                if (reducedRef.current) {
                    placeThumb(el, target);
                } else {
                    widthAnimation.current = springThumb(el, target, motionTokens.springs.thumb);
                }
            }
        }
    };

    const endDrag = (event: PointerEvent<HTMLDivElement>, cancelled: boolean): void => {
        const drag = dragRef.current;
        if (drag === null || drag.pointerId !== event.pointerId) return;
        dragRef.current = null;

        const root = event.currentTarget;
        releasePointer(root, event.pointerId);
        root.removeAttribute("data-dragging");
        for (const el of segmentRefs.current) {
            if (el !== null) setPressed(el, false);
        }

        if (!drag.moved) return;
        // The click that follows this pointerup belongs to the drag, not to a
        // segment. It clears itself once the event loop turns.
        window.setTimeout(() => {
            suppressClickRef.current = false;
        }, 0);

        const target = cancelled ? selectedIndexRef.current : drag.hoverIndex;
        const option = options[target];
        if (option === undefined || target === selectedIndexRef.current) {
            moveThumb(selectedIndexRef.current, true);
            return;
        }
        onChange(option.value);
        // The `value` effect animates the accepted change. If the owner
        // rejected it, the thumb returns to the value that is still selected.
        requestAnimationFrame(() => {
            if (selectedIndexRef.current !== target) {
                moveThumb(selectedIndexRef.current, true);
            }
        });
    };

    const handleClick = (index: number): void => {
        if (suppressClickRef.current) return;
        select(index);
    };

    const tabStopIndex = isEnabled(selectedIndex)
        ? selectedIndex
        : options.findIndex((_, i) => isEnabled(i));

    return (
        <div
            ref={rootRef}
            role="radiogroup"
            aria-label={ariaLabel}
            aria-labelledby={labelledBy}
            aria-describedby={ariaDescribedBy}
            aria-disabled={disabled || undefined}
            className={clsx(styles.root, className)}
            data-size={size}
            data-tone={tone}
            data-full-width={fullWidth || undefined}
            data-disabled={disabled || undefined}
            onKeyDown={handleKeyDown}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={(event) => endDrag(event, false)}
            onPointerCancel={(event) => endDrag(event, true)}
        >
            <div aria-hidden="true" ref={thumbRef} className={styles.thumb} />
            {options.map((option, index) => {
                const selected = index === selectedIndex;
                const inert = !isEnabled(index);
                const Icon = option.icon;
                const showLabel = !iconOnly && option.label !== undefined;
                return (
                    <button
                        key={option.value}
                        ref={(el) => {
                            segmentRefs.current[index] = el;
                        }}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        aria-label={option.ariaLabel}
                        title={iconOnly ? option.ariaLabel : undefined}
                        tabIndex={index === tabStopIndex ? 0 : -1}
                        disabled={inert}
                        className={clsx(pressable.pressable, styles.segment)}
                        data-selected={selected || undefined}
                        data-icon-only={iconOnly || undefined}
                        data-hit-area={size === "sm" ? "" : undefined}
                        onClick={() => handleClick(index)}
                        {...pressHandlers<HTMLButtonElement>({}, inert)}
                    >
                        {Icon !== undefined && (
                            <Icon size={iconSize[size]} strokeWidth={2.25} aria-hidden="true" />
                        )}
                        {showLabel && <span className={styles.label}>{option.label}</span>}
                    </button>
                );
            })}
        </div>
    );
}
